import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  toast 
} from '@etherisc/ui-kit';
import { productService } from '../../services/productService';
import { apiClient } from '../../services/api';
import type { Product, ProductConfig } from '../../types/product';
import { PRODUCT_STATUS_LABELS } from '../../types/product';
import { ProductConfigForm } from '../../components/product/ProductConfigForm';

import { ChevronLeftIcon, FileTextIcon, EditIcon, AlertTriangleIcon, Download } from 'lucide-react';
import { handleGenerateProductMethodologyPdf } from '../../utils/pdfUtils';

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [cronExpression, setCronExpression] = useState<string>('');

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await productService.getProductById(id);

        setProduct(response.product);

        // Load automation schedule for this product
        try {
          const scheduleRes = await apiClient.getAutomationSchedules('product', id);
          const schedules = scheduleRes?.schedules || [];
          const assessSchedule = schedules.find((s: { taskType: string }) => s.taskType === 'claims_assessment');
          if (assessSchedule) {
            setCronExpression(assessSchedule.cronExpression);
          }
        } catch {
          // Non-critical
        }
      } catch (error: unknown) {
        console.error('Error fetching product:', error);
        const errorMessage = (error as Error)?.message || 'Failed to load product.';
        setError(errorMessage);
        toast.error('Load Failed', { description: errorMessage });
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleGenerateMethodologyPDF = async () => {
    if (!product) return;
    
    setIsGeneratingPDF(true);
    try {
      await handleGenerateProductMethodologyPdf(product.id, product.name);
    } catch (error) {
      console.error('Error generating methodology PDF:', error);
      // Error is already handled by the utility function
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const getProductConfig = (): ProductConfig | null => {
    if (!product?.productConfig) return null;
    
    // Handle wrapped productConfig structure from backend
    const config = product.productConfig as unknown;
    
    // Type guard for wrapped format
    if (config && typeof config === 'object' && 'data' in config && 'schemaId' in config && 'schemaVersion' in config) {
      // Wrapped format: { data: ProductConfig, schemaId: string, schemaVersion: string }
      const wrappedConfig = config as { data: ProductConfig; schemaId: string; schemaVersion: string };
      const unwrappedConfig = { ...wrappedConfig.data, _version: wrappedConfig.schemaVersion };
      return unwrappedConfig;
    }
    
    // Direct format (fallback)
    return product.productConfig as ProductConfig;
  };

  const getSchemaVersion = (): string => {
    const config = getProductConfig();
    return config?._version || 'v1.5.0'; // Fallback - should use dynamic version
  };

  const isLegacyConfig = (): boolean => {
    // Since we only handle v1.4.0, assume no legacy configs
    return false;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="text-center py-16">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <Button onClick={() => navigate('/admin/products')}>
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="text-center py-16">
          <p className="text-gray-600 text-lg mb-4">Product not found</p>
          <Button onClick={() => navigate('/admin/products')}>
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  const productConfig = getProductConfig();
  const isLegacy = isLegacyConfig();

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/products')}
            className="flex items-center gap-2"
          >
            <ChevronLeftIcon size={16} />
            Back to Products
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileTextIcon size={32} />
              {product.name}
            </h1>
            <div className="text-gray-600 mt-1 flex items-center gap-4 text-sm">
              <span>{product.code}</span>
              <span>•</span>
              <span>Version {product.version || '1.0.0'}</span>
              <span>•</span>
              <span>Created {new Date(product.createdAt).toLocaleDateString()}</span>
              <span>•</span>
              <span>Updated {new Date(product.updatedAt).toLocaleDateString()}</span>
              <Badge className={getStatusColor(product.status)}>
                {PRODUCT_STATUS_LABELS[product.status] || product.status}
              </Badge>
              {product.productConfig && (
                <Badge variant="secondary" className="text-xs">
                  {getSchemaVersion()}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleGenerateMethodologyPDF}
            disabled={isGeneratingPDF}
            className="flex items-center gap-2"
          >
            <Download size={16} />
            {isGeneratingPDF ? 'Generating...' : 'Methodology PDF'}
          </Button>
          <Button
            onClick={() => navigate(`/admin/products/${product.id}/edit`)}
            className="flex items-center gap-2"
          >
            <EditIcon size={16} />
            Edit Product
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Legacy Configuration Warning */}
        {isLegacy && (
          <Card>
            <CardContent className="pt-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertTriangleIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Legacy Configuration Detected</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>This product uses an older configuration format (v1.3.0 or earlier). To view and edit the full configuration, please:</p>
                      <ol className="list-decimal list-inside mt-2 space-y-1">
                        <li>Edit the product to migrate it to the new v1.4.0 format</li>
                        <li>Update loading factors to use administrative units</li>
                        <li>Link stakeholder organizations with proper organization IDs</li>
                      </ol>
                      <div className="mt-4">
                        <Button
                          onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                          className="flex items-center gap-2"
                          size="sm"
                        >
                          <EditIcon size={14} />
                          Migrate to v1.4.0
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Configuration (v1.4.0) */}
        {productConfig ? (
          <ProductConfigForm
            productConfig={productConfig}
            onChange={() => {}} // Read-only mode
            productName={product.name}
            productCode={product.code}
            productDescription={product.description}
            productStatus={product.status}
            productTags={product.tags}
            productVersion={product.version}
            quotationScript={product.quotationScript}
            claimsScript={product.claimsScript}
            onProductFieldChange={() => {}} // Read-only mode
            errors={[]}
            readOnly
            cronExpression={cronExpression}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Product Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <FileTextIcon size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-lg">No configuration available</p>
                <p className="text-sm mt-2">This product doesn't have a configuration yet, or the configuration format is not supported.</p>
                <div className="mt-4">
                  <Button
                    onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                    className="flex items-center gap-2"
                  >
                    <EditIcon size={16} />
                    Configure Product
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Raw Configuration Data (for debugging) */}
        {product.productConfig && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Raw Configuration Data</CardTitle>
            </CardHeader>
            <CardContent>
              <details className="group">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 select-none">
                  Click to view raw JSON configuration
                </summary>
                <pre className="mt-4 p-4 bg-gray-50 rounded-lg text-xs overflow-auto max-h-96">
                  {JSON.stringify(product.productConfig, null, 2)}
                </pre>
              </details>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}